import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  TextField,
  CircularProgress,
  Grid
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const MetaAdsModal = ({ open, onClose, whatsApp, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ads, setAds] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);

  // Form de novo anúncio
  const [newName, setNewName] = useState("");
  const [newId, setNewId] = useState("");
  const [newService, setNewService] = useState("");

  useEffect(() => {
    if (open && whatsApp?.id) {
      fetchAdsReport();
    }
  }, [open, whatsApp]);

  const fetchAdsReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/${whatsApp.id}/ads-report`);
      setAds(data.ads || []);
      setTotalLeads(data.totalAdsLeads || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAd = () => {
    if (!newName.trim() || !newId.trim()) {
      toast.warn("Preencha o Nome e o ID do Anúncio.");
      return;
    }
    const newEntry = {
      id: newId.trim(),
      name: newName.trim(),
      service: newService.trim(),
      totalLeads: 0,
      activeLeads: 0,
      closedLeads: 0
    };
    setAds(prev => [...prev, newEntry]);
    setNewName("");
    setNewId("");
    setNewService("");
  };

  const handleRemoveAd = index => {
    setAds(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const adsToSave = ads.map(a => ({
        id: a.id,
        name: a.name,
        service: a.service || ""
      }));
      await api.put(`/whatsapp/${whatsApp.id}`, {
        metaAds: JSON.stringify(adsToSave)
      });
      toast.success("Anúncios do Meta Ads atualizados com sucesso!");
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography style={{ backgroundColor: "#1877F2", color: "#fff", padding: "12px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "1.2rem" }}>🎯</span>
            <Typography variant="h6" style={{ fontWeight: "bold", fontSize: "1.1rem", color: "#fff" }}>
              Meta Ads & Conversões — {whatsApp?.name}
            </Typography>
          </div>
          <IconButton size="small" onClick={onClose} style={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers style={{ padding: "20px", backgroundColor: "#f8fafc" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* CARD DE RESUMO */}
            <Paper variant="outlined" style={{ padding: "14px 20px", backgroundColor: "#eff6ff", borderColor: "#bfdbfe", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Typography variant="caption" style={{ color: "#1e40af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Total de Conversas Originadas por Anúncios
                </Typography>
                <Typography variant="h4" style={{ fontWeight: "bold", color: "#1e3a8a", marginTop: 2 }}>
                  {totalLeads} <span style={{ fontSize: "0.95rem", fontWeight: 500 }}>leads recebidos</span>
                </Typography>
              </div>
              <span style={{ fontSize: "2rem" }}>📈</span>
            </Paper>

            {/* TABELA DE ANÚNCIOS */}
            <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#334155", marginTop: 4 }}>
              📋 Anúncios Mapeados & Volume de Conversas
            </Typography>

            {ads.length > 0 ? (
              <Paper variant="outlined" style={{ overflow: "hidden", borderRadius: 8 }}>
                <Table size="small">
                  <TableHead style={{ backgroundColor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold", color: "#334155" }}>Nome do Anúncio</TableCell>
                      <TableCell style={{ fontWeight: "bold", color: "#334155" }}>ID Meta Ads</TableCell>
                      <TableCell style={{ fontWeight: "bold", color: "#334155" }}>Serviço Vinculado</TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold", color: "#334155" }}>Conversas Recebidas</TableCell>
                      <TableCell align="right" style={{ fontWeight: "bold", color: "#334155" }}>Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ads.map((ad, idx) => (
                      <TableRow key={ad.id || idx} hover>
                        <TableCell>
                          <Chip
                            label={ad.name}
                            size="small"
                            style={{
                              backgroundColor: "#1877F2",
                              color: "#fff",
                              fontWeight: "bold",
                              fontSize: "0.75rem"
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ fontFamily: "monospace", fontSize: "0.85rem", color: "#0f172a" }}>
                          {ad.id}
                        </TableCell>
                        <TableCell style={{ fontSize: "0.85rem", color: "#475569" }}>
                          {ad.service || "—"}
                        </TableCell>
                        <TableCell align="center">
                          <span style={{ fontWeight: "bold", fontSize: "0.95rem", color: "#0f172a" }}>
                            {ad.totalLeads || 0}
                          </span>
                          {(ad.totalLeads > 0) && (
                            <span style={{ fontSize: "0.75rem", color: "#64748b", marginLeft: 4 }}>
                              ({ad.activeLeads || 0} ativas / {ad.closedLeads || 0} resolvidas)
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleRemoveAd(idx)} style={{ color: "#ef4444" }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Paper variant="outlined" style={{ padding: 20, textAlign: "center", backgroundColor: "#fff" }}>
                <Typography variant="body2" style={{ color: "#64748b" }}>
                  Nenhum anúncio cadastrado nesta conexão. Adicione um novo anúncio abaixo para iniciar o rastreamento!
                </Typography>
              </Paper>
            )}

            {/* FORMULÁRIO DE NOVO ANÚNCIO */}
            <Paper variant="outlined" style={{ padding: 14, borderRadius: 8, backgroundColor: "#fff" }}>
              <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 10, color: "#1e293b" }}>
                ➕ Adicionar Novo Anúncio da Meta
              </Typography>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  label="Identificador / Nome"
                  placeholder="ex: ad-ocupacional"
                  size="small"
                  variant="outlined"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  style={{ flex: 1, minWidth: 160 }}
                />
                <TextField
                  label="ID do Anúncio (Meta Ads)"
                  placeholder="ex: 120250144985680219"
                  size="small"
                  variant="outlined"
                  value={newId}
                  onChange={e => setNewId(e.target.value)}
                  style={{ flex: 1, minWidth: 180 }}
                />
                <TextField
                  label="Serviço de Auto-Categorização"
                  placeholder="ex: Exames Ocupacionais (ASO)"
                  size="small"
                  variant="outlined"
                  value={newService}
                  onChange={e => setNewService(e.target.value)}
                  style={{ flex: 1, minWidth: 180 }}
                />
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={handleAddAd}
                  disabled={!newName.trim() || !newId.trim()}
                  style={{
                    backgroundColor: (!newName.trim() || !newId.trim()) ? "#cbd5e1" : "#1877F2",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: "bold"
                  }}
                >
                  Adicionar
                </Button>
              </div>
            </Paper>
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 20px" }}>
        <Button onClick={onClose} color="secondary" variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || loading}
          style={{ backgroundColor: "#1877F2", color: "#fff", fontWeight: "bold" }}
        >
          {saving ? "Salvando..." : "Salvar Alterações"}
          {saving && <CircularProgress size={18} style={{ marginLeft: 8, color: "#fff" }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetaAdsModal;
